import styled, { css } from 'styled-components'

export const fontSizeMapping = {
  small: css`
    p {
      font-size: 8px;
      line-height: 12px;
    }

    h3 {
      font-size: 12px;
      line-height: 16px;
    }
  `,
  regular: css`
    @media (max-width: 1023px) {
      p {
        font-size: 8px;
        line-height: 12px;
      }

      h3 {
        font-size: 12px;
        line-height: 16px;
      }
    }

    @media (min-width: 1024px) and (max-width: 1599px) {
      h3 {
        font-size: 24px;
        line-height: 32px;
      }
    }
  `
} as const

export const headingSizeMapping = {
  small: css`
    p {
      font-size: 10px;
      line-height: 14px;
    }

    h2 {
      font-size: 16px;
      line-height: 20px;
    }
  `,
  regular: css`
    p {
      font-size: 10px;
      line-height: 14px;
    }

    h2 {
      font-size: 16px;
      line-height: 20px;
    }

    @media (min-width: 1023px) {
      p {
        font-size: 12px;
        line-height: 16px;
      }

      h2 {
        font-size: 24px;
        line-height: 32px;
      }
    }
    }
  `
} as const

export const imageSizeMapping = {
  small: css`
    height: 74px;

    @media (min-width: 699px) {
      height: 106px;
    }
  `,
  regular: css`
    @media (min-width: 1023px) and (max-width: 1599px) {
      height: 124px;
    }

    @media (min-width: 1600px) {
      height: 160px;
    }
  `
} as const

const chartSpacingMapping = {
  small: css`
    @media (max-width: 768px) {
      svg {
        margin-top: -20%;
      }
    }

    @media (min-width: 769px) and (max-width: 1023px) {
      svg {
        margin-top: -15%;
      }
    }

    @media (min-width: 1024px) and (max-width: 1600px) {
      margin-top: -16%;
    }

    @media (min-width: 1601px) {
      margin-top: -16%;
    }
  `,
  regular: css`
    @media (max-width: 768px) {
      svg {
        margin-top: -20%;
      }
    }

    @media (min-width: 769px) and (max-width: 1023px) {
      svg {
        margin-top: -20%;
      }
    }

    @media (min-width: 1024px) and (max-width: 1600px) {
      margin-top: -6%;
    }

    @media (min-width: 1601px) {
      margin-top: 3%;
    }
  `
}

export const ChartSection = styled.section`
  display: flex;
  flex-direction: column;
`

export const CardContainer = styled.div<{ $cardMinWidth?: string }>`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 1rem;
  margin: 16px 0;

  @media (min-width: 700px) and (max-width: 1023px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    ${({ $cardMinWidth }) =>
      !!$cardMinWidth
        ? `grid-template-columns: repeat(auto-fit, minmax(${$cardMinWidth}, 1fr))`
        : `grid-template-columns: repeat(2, 1fr)`};
  }
`

export const CardHeader = styled.header`
  display: flex;
  gap: 0.5rem;
  justify-content: space-between;

  > div {
    height: max-content;
    width: max-content;
  }

  button {
    border-color: #303032;
    color: #303032;
    background-color: transparent;
    width: 32px;
    height: 32px;
    min-width: 32px;

    &:hover {
      border-color: #dedede;
    }
  }
`

export const CardHeaderContent = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  h2 {
    font-weight: 600;
    margin: 0;
    text-align: left;
  }

  p {
    margin: 0;
    text-align: justify;
  }
`

export const CardContentGrid = styled.section`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-align-items: center;
  grid-gap: 0.5rem;
  margin-top: auto;
`

export const CardContentRow = styled.section`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

export const CardResultContent = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  @media (min-width: 769px) {
    gap: 0.5rem;
  }
`

export const CardSubResultContent = styled.aside`
  display: flex;
  flex-direction: column;
`

export const Image = styled.img`
  object-fit: cover;
  width: auto;
  height: 74px;
`

export const ChartWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: auto;

  @media (max-width: 768px) {
    padding-bottom: 8px;
  }

  @media (min-width: 769px) and (max-width: 1023px) {
    padding-bottom: 12px;
  }

  @media (min-width: 1024px) and (max-width: 1600px) {
    padding-bottom: 20px;
  }
`

export const Card = styled.article<{
  $contentSize: keyof typeof fontSizeMapping
}>`
  padding: 0.75rem;
  border: 1px solid #bcbcbe;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  ${CardHeaderContent} {
    ${({ $contentSize }) =>
      headingSizeMapping[$contentSize] || headingSizeMapping.small}
  }

  ${CardSubResultContent} {
    ${({ $contentSize }) =>
      fontSizeMapping[$contentSize] || fontSizeMapping.small}
  }

  ${Image} {
    ${({ $contentSize }) =>
      imageSizeMapping[$contentSize] || imageSizeMapping.small}
  }
`
